/**
 * Repository Card Component
 * Displays individual GitLab repository information with webhook management
 */

import { useState } from 'react';
import { ExternalLinkIcon, GitBranchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { GitlabRepository, GitlabWebhook } from '@/lib/types/gitlab';

import { WebhookStatusIndicator } from './WebhookStatusIndicator';

export type RepositoryCardProps = {
  /** Repository data */
  repository: GitlabRepository;
  /** Webhook data (null if no webhook) */
  webhook?: GitlabWebhook | null;
  /** Webhook operation state */
  webhookOperation?: 'adding' | 'removing' | undefined;
  /** Callback to add webhook */
  onAddWebhook?: () => void;
  /** Callback to remove webhook */
  onRemoveWebhook?: () => void;
};

export const RepositoryCard = ({
  repository,
  webhook,
  webhookOperation,
  onAddWebhook,
  onRemoveWebhook,
}: RepositoryCardProps): JSX.Element => {
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const handleRemoveClick = () => {
    setShowRemoveDialog(true);
  };

  const handleConfirmRemove = () => {
    onRemoveWebhook?.();
    setShowRemoveDialog(false);
  };

  const isWebhookLoading =
    webhookOperation === 'adding' || webhookOperation === 'removing';

  return (
    <>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 space-y-3">
            {/* Repository header */}
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-lg truncate">
                    {repository.fullName}
                  </h3>
                  <a
                    href={repository.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ExternalLinkIcon className="h-4 w-4" />
                  </a>
                </div>

                {repository.description !== null &&
                  repository.description !== '' && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {repository.description}
                    </p>
                  )}
              </div>
            </div>

            {/* Repository metadata */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <GitBranchIcon className="h-3 w-3" />
                <span>{repository.defaultBranch}</span>
              </div>

              <span>Updated recently</span>
            </div>

            {/* Webhook status and actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Webhook:</span>
                <WebhookStatusIndicator
                  webhook={webhook ?? null}
                  isRefreshing={isWebhookLoading}
                />
              </div>

              <div className="flex items-center gap-2">
                {repository.hasWebhook ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveClick}
                    disabled={isWebhookLoading}
                    className="text-destructive hover:text-destructive"
                  >
                    {webhookOperation === 'removing'
                      ? 'Removing...'
                      : 'Remove Webhook'}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAddWebhook}
                    disabled={isWebhookLoading}
                  >
                    {webhookOperation === 'adding'
                      ? 'Adding...'
                      : 'Add Webhook'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Remove confirmation dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Webhook</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the webhook from{' '}
              <strong>{repository.fullName}</strong>?
              <br />
              <br />
              This will delete the webhook from the GitLab repository and stop
              sending events to Anyshift.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRemoveDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmRemove} variant="destructive">
              Remove Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
